/**
 * SYSTEM SETTINGS MODULE — public embed keys, both kinds (items 28 and 124).
 *
 * ONE PANEL, TWO KINDS, chosen by the `kind` prop and fixed for the life of the
 * key. It keeps the `Pos…` name because the table does (`pos_embed_keys`), and
 * the table keeps it because a sample key is the same row shape — see the
 * header of `posEmbedService.js` and migration 0097.
 *
 *   kind="pos"    — one key per distributor, pasted behind that distributor's
 *                   login. Ship-to is DERIVED from the delivery contact, so the
 *                   picker is REQUIRED.
 *   kind="sample" — a public sample-request form. Ship-to is free text typed by
 *                   the requester, so there is NO delivery-contact picker at
 *                   all: `pos_embed_keys_contact_by_kind_check` refuses a
 *                   sample row that carries one, and a CHECK violation reaches
 *                   the user as a Postgres constraint name. The UI must not be
 *                   able to express a row the database rejects.
 *
 * The panel is deliberately blunt about the two things that get this wrong in
 * practice:
 *   • a key with no allowed website is DISABLED, not "open to everyone";
 *   • the key is public by construction, so it is presented as an identifier to
 *     be revoked rather than a secret to be protected.
 * For sample keys there is a third: the form gives away free product, so the
 * panel says out loud that nothing ships without an approval.
 */

import { useCallback, useEffect, useState } from "react";
import {
  listEmbedKeys, createEmbedKey, updateEmbedKey, deleteEmbedKey,
  generateMissingEmbedKeys, embedSnippet, embedLimitBounds,
} from "../utils/posEmbedService.js";
import { loadPOSDeliveryContacts } from "../../../shared/utils/referenceDataService.js";

const cell = { padding: "9px 10px", borderBottom: "1px solid var(--border)", verticalAlign: "top" };

// Same shape the edge function and send-email use. Deliberately loose — the
// real deliverability check is the provider's, and a stricter pattern rejects
// addresses that work. The DB check (migration 0087) is looser still.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const contactLabel = (c) =>
  [c.distributor, c.warehouse_name, c.state].filter(Boolean).join(" — ");

const COPY = {
  pos: {
    badge: "POS order",
    one: "order form",
    many: "order forms",
    Create: "Create form",
    nameLabel: "Name this form",
    namePlaceholder: "e.g. Breakthru — Illinois",
    notifyHead: "Email new orders to",
    notifyHint: "Leave blank for none — the order just waits in POS Orders.",
    empty: "No order forms yet.",
    findLabel: "Find a distributor",
    findPlaceholder: "Type a distributor or state",
    deleteWarn: (label) =>
      `Delete "${label}"? Any page still using it stops working immediately. Orders already placed are kept.`,
  },
  sample: {
    badge: "Sample request",
    one: "request form",
    many: "request forms",
    Create: "Create request form",
    nameLabel: "Name this form",
    namePlaceholder: "e.g. Trade site — sample requests",
    notifyHead: "Email new requests to",
    notifyHint: "Leave blank for none — but then nothing tells you a request is waiting for approval.",
    empty: "No sample request forms yet.",
    findLabel: "Find a form",
    findPlaceholder: "Type a form name",
    deleteWarn: (label) =>
      `Delete "${label}"? Any page still using it stops working immediately. Requests already submitted are kept.`,
  },
};

export default function PosEmbedKeysPanel({ kind = "pos" }) {
  const isSample = kind === "sample";
  const t = COPY[isSample ? "sample" : "pos"];

  const [keys, setKeys]         = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);
  const [copied, setCopied]     = useState(null);
  const [note, setNote]         = useState(null);
  const [filter, setFilter]     = useState("");

  const [newLabel, setNewLabel]     = useState("");
  const [newContact, setNewContact] = useState("");

  const reload = useCallback(async () => {
    // The delivery address book is only meaningful for POS keys — a sample key
    // has no contact to bind, so the read is skipped rather than fetched and
    // ignored.
    const [k, c] = await Promise.all([
      listEmbedKeys(kind),
      isSample ? Promise.resolve([]) : loadPOSDeliveryContacts(),
    ]);
    setKeys(k);
    setContacts(c);
  }, [kind, isSample]);

  useEffect(() => {
    setLoading(true);
    reload()
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [reload]);

  const run = async (fn) => {
    setBusy(true);
    setErr(null);
    setNote(null);
    try { await fn(); await reload(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const add = () => run(async () => {
    // `kind` is sent on the INSERT and nowhere else — it is what decides which
    // invariant protects the key, so it is chosen once and never edited.
    // A sample key deliberately sends NO delivery_contact_id.
    await createEmbedKey(
      isSample
        ? { kind: "sample", label: newLabel }
        : { kind: "pos", label: newLabel, delivery_contact_id: newContact }
    );
    setNewLabel("");
    setNewContact("");
  });

  const generateAll = () => run(async () => {
    const { created, skipped } = await generateMissingEmbedKeys(contacts);
    setNote(
      created === 0
        ? "Every distributor already has an order form."
        : `Created ${created} order form${created === 1 ? "" : "s"}${skipped ? `, skipped ${skipped} that already had one` : ""}. ` +
          "Each one is switched off until you add the website it lives on."
    );
  });

  const missingCount = isSample ? 0 : contacts.filter(
    (c) => !keys.some((k) => k.delivery_contact_id === c.id)
  ).length;

  const shown = filter.trim()
    ? keys.filter((k) => {
        const c = contacts.find((x) => x.id === k.delivery_contact_id);
        const hay = `${k.label} ${c ? contactLabel(c) : ""}`.toLowerCase();
        return hay.includes(filter.trim().toLowerCase());
      })
    : keys;

  const copy = async (key) => {
    try {
      await navigator.clipboard.writeText(embedSnippet(key.public_key, key.kind ?? kind));
      setCopied(key.id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setErr("Couldn't copy — select the snippet and copy it manually.");
    }
  };

  if (loading) return <p className="muted">Loading…</p>;

  return (
    <div>
      {isSample ? (
        <>
          <p className="muted">
            Put one of these on any page where you want people to be able to ask
            for samples — a trade page, a rep portal, a landing page for a show.
            Whoever fills it in types the address the samples should go to, so
            you can send to a prospect or an event, not just to accounts you
            already have on file.
          </p>
          <p className="muted">
            <strong>Nothing ships until you say so.</strong> Every request that
            comes through one of these forms arrives in Sample Orders marked
            <em> awaiting approval</em>, and it can’t be marked shipped until
            someone approves it. That’s deliberate: the form is public, so if the
            snippet ever ends up somewhere you didn’t put it, the worst that
            happens is a queue you have to clear.
          </p>
          <p className="muted">
            Requests arrive in Sample Orders. Put an address in{" "}
            <strong>Email new requests to</strong> if you want to be told the
            moment one comes in — with an approval step in the way, a request
            nobody looks at is a request that never ships.
          </p>
        </>
      ) : (
        <>
          <p className="muted">
            Give each distributor their own order form. Put each snippet on the page
            that distributor sees after logging in to your website — the key is what
            identifies them, so every order that comes through it ships to that
            distributor’s address automatically and they can’t order as anyone else.
          </p>
          <p className="muted">
            Orders arrive in POS Orders. If you’d rather be told the moment one comes
            in, put an address in <strong>Email new orders to</strong> — it’s per form,
            so the alert says which distributor ordered. Leave it blank and nobody is
            emailed, which is how every form starts out.
          </p>
        </>
      )}

      {!isSample && contacts.length === 0 && (
        <p className="muted">
          Add a delivery address first — a form has to know where its orders ship.
        </p>
      )}

      {missingCount > 0 && (
        <div style={{ margin: "12px 0" }}>
          <button type="button" className="btn secondary" disabled={busy} onClick={generateAll}>
            Create forms for all {missingCount} remaining distributor{missingCount === 1 ? "" : "s"}
          </button>
          <p className="muted gs-foot">
            One per delivery address that doesn’t have one yet. They arrive
            switched off until you add the website each belongs on.
          </p>
        </div>
      )}

      {note && <p className="muted">{note}</p>}

      {/* new key */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", margin: "14px 0 18px" }}>
        <label style={{ flex: "1 1 220px" }}>
          <span>{t.nameLabel}</span>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder={t.namePlaceholder}
          />
        </label>

        {/* REQUIRED for a POS key, ABSENT for a sample one. Not disabled, not
            hidden-but-submitted: a sample row with a delivery contact violates
            pos_embed_keys_contact_by_kind_check, so the field does not exist. */}
        {!isSample && (
          <label style={{ flex: "1 1 240px" }}>
            <span>Ships to</span>
            <select value={newContact} onChange={(e) => setNewContact(e.target.value)}>
              <option value="">— Select a delivery address —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{contactLabel(c)}</option>
              ))}
            </select>
          </label>
        )}

        <button
          type="button"
          className="btn"
          disabled={busy || !newLabel.trim() || (!isSample && !newContact)}
          onClick={add}
        >
          {t.Create}
        </button>
      </div>

      {err && <p className="error">{err}</p>}

      {keys.length > 8 && (
        <label style={{ display: "block", maxWidth: 320, margin: "0 0 12px" }}>
          <span>{t.findLabel}</span>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t.findPlaceholder}
          />
        </label>
      )}

      {keys.length === 0 ? (
        <p className="muted">{t.empty}</p>
      ) : shown.length === 0 ? (
        <p className="muted">No forms match “{filter}”.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...cell, textAlign: "left" }}>Form</th>
                <th style={{ ...cell, textAlign: "left" }}>Allowed website</th>
                <th style={{ ...cell, textAlign: "left" }}>{t.notifyHead}</th>
                <th style={{ ...cell, textAlign: "left" }}>Limits</th>
                <th style={{ ...cell, textAlign: "left" }}>Status</th>
                <th style={{ ...cell, textAlign: "left" }}>Snippet</th>
                <th style={cell} />
              </tr>
            </thead>
            <tbody>
              {shown.map((k) => (
                <KeyRow
                  key={k.id}
                  row={k}
                  kind={k.kind ?? kind}
                  contacts={contacts}
                  busy={busy}
                  copied={copied === k.id}
                  onCopy={() => copy(k)}
                  onSave={(patch) => run(() => updateEmbedKey(k.id, patch, k.kind ?? kind))}
                  onDelete={() => run(() => deleteEmbedKey(k.id, k.kind ?? kind))}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KeyRow({ row, kind, contacts, busy, copied, onCopy, onSave, onDelete }) {
  const isSample = kind === "sample";
  const t = COPY[isSample ? "sample" : "pos"];
  const [origins, setOrigins] = useState((row.allowed_origins ?? []).join("\n"));
  const [notify, setNotify]   = useState(row.notify_email ?? "");
  const [notifyErr, setNotifyErr] = useState(null);
  const contact = contacts.find((c) => c.id === row.delivery_contact_id);
  const live = row.is_active && (row.allowed_origins ?? []).length > 0;

  // Item 86. Capability probe rather than an unconditional input, because the
  // column arrives in migration 0087 and `posEmbedService` reads an explicit
  // column list: until BOTH have landed, an input here would accept a typed
  // address, appear to save, and drop it. Saying "not available" is the loud
  // version of the same state, and it disappears on its own once the field is
  // in the service's SELECT.
  const notifySupported = Object.prototype.hasOwnProperty.call(row, "notify_email");

  return (
    <tr>
      <td style={cell}>
        <div style={{ fontWeight: 600 }}>{row.label}</div>
        {/* The kind at a glance. It cannot be changed here — see SAVEABLE in
            posEmbedService — so it reads as a fact about the key, not a control. */}
        <div style={{ margin: "3px 0 2px" }}>
          <span
            style={{
              fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 999,
              border: "1px solid var(--border)", background: "var(--sunken)", color: "var(--muted)",
            }}
          >
            {t.badge}
          </span>
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {isSample
            ? "Ships to an address the requester types — needs approval first"
            : contact ? contactLabel(contact) : "Delivery address removed"}
        </div>
      </td>

      <td style={cell}>
        <textarea
          value={origins}
          disabled={busy}
          rows={2}
          style={{ width: "100%", minWidth: 200, fontSize: 12 }}
          placeholder="https://yourbrand.com"
          onChange={(e) => setOrigins(e.target.value)}
          onBlur={() => {
            const list = origins.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
            const current = row.allowed_origins ?? [];
            if (list.join("|") !== current.join("|")) onSave({ allowed_origins: list });
          }}
        />
        <div className="muted" style={{ fontSize: 11 }}>
          One per line. Empty means the form is switched off.
        </div>
      </td>

      <td style={cell}>
        {notifySupported ? (
          <>
            <input
              type="email"
              value={notify}
              disabled={busy}
              style={{ width: "100%", minWidth: 170, fontSize: 12 }}
              placeholder="Leave blank for none"
              onChange={(e) => { setNotify(e.target.value); setNotifyErr(null); }}
              onBlur={() => {
                const v = notify.trim();
                if (v === (row.notify_email ?? "")) return;
                if (v && !EMAIL_RE.test(v)) {
                  setNotifyErr("That doesn’t look like an email address.");
                  return;
                }
                setNotifyErr(null);
                // Blank saves as null, not "" — the DB check treats the empty
                // string as invalid so that "nobody is told" has exactly one
                // representation.
                onSave({ notify_email: v || null });
              }}
            />
            <div className="muted" style={{ fontSize: 11 }}>
              {notifyErr
                ? <span className="error">{notifyErr}</span>
                : t.notifyHint}
            </div>
          </>
        ) : (
          <span className="muted" style={{ fontSize: 11 }}>
            Not available yet (migration 0087).
          </span>
        )}
      </td>

      <td style={cell}>
        {isSample ? (
          // The ceilings migration 0097 put on sample keys. Editable because
          // they are the one control a tenant can tighten themselves: the
          // approval gate stops free product leaving, and these decide how big
          // a mess a leaked key can make before anyone looks at the queue. The
          // DB clamps anything higher (`least()`), so the only useful direction
          // is down, and the maximums say so rather than silently winning.
          <SampleLimits row={row} busy={busy} onSave={onSave} />
        ) : (
          <div className="muted" style={{ fontSize: 12 }}>
            {row.max_lines} items · {row.max_qty_per_line}/item · {row.max_orders_per_hour}/hr
          </div>
        )}
      </td>

      <td style={cell}>
        <span style={{ fontSize: 12, fontWeight: 600, color: live ? "var(--success)" : "var(--muted)" }}>
          {!row.is_active ? "Revoked" : live ? "Live" : "No website set"}
        </span>
      </td>

      <td style={cell}>
        <code style={{ fontSize: 11, wordBreak: "break-all" }}>{embedSnippet(row.public_key, kind)}</code>
        <div>
          <button type="button" className="btn link" disabled={busy} onClick={onCopy}>
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </td>

      <td style={{ ...cell, whiteSpace: "nowrap" }}>
        <button
          type="button"
          className="btn secondary"
          disabled={busy}
          onClick={() => onSave({ is_active: !row.is_active })}
        >
          {row.is_active ? "Revoke" : "Re-enable"}
        </button>{" "}
        <button
          type="button"
          className="btn danger"
          disabled={busy}
          onClick={() => {
            if (window.confirm(t.deleteWarn(row.label))) onDelete();
          }}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

/** The three per-key ceilings a sample form is held to (migration 0097). */
function SampleLimits({ row, busy, onSave }) {
  const bounds = embedLimitBounds("sample");
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <LimitField field="max_lines"           label="products per request" bounds={bounds.max_lines}           row={row} busy={busy} onSave={onSave} />
      <LimitField field="max_qty_per_line"    label="units per product"    bounds={bounds.max_qty_per_line}    row={row} busy={busy} onSave={onSave} />
      <LimitField field="max_orders_per_hour" label="requests per hour"    bounds={bounds.max_orders_per_hour} row={row} busy={busy} onSave={onSave} />
    </div>
  );
}

function LimitField({ field, label, bounds, row, busy, onSave }) {
  const [lo, hi] = bounds;
  const [v, setV]     = useState(String(row[field] ?? ""));
  const [msg, setMsg] = useState(null);
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, fontSize: 11 }}>
      <input
        type="number"
        min={lo}
        max={hi}
        value={v}
        disabled={busy}
        style={{ width: 62, fontSize: 12, margin: 0 }}
        onChange={(e) => { setV(e.target.value); setMsg(null); }}
        onBlur={() => {
          const n = Number(v);
          if (n === row[field]) return;
          // Checked here so the message points at the box, and again in the
          // service, which is the side that actually composes the write.
          if (!Number.isInteger(n) || n < lo || n > hi) {
            setMsg(`${lo}–${hi}`);
            return;
          }
          setMsg(null);
          onSave({ [field]: n });
        }}
      />
      <span className="muted">
        {label} <span style={{ opacity: 0.75 }}>(max {hi})</span>
        {msg ? <span className="error"> {msg}</span> : null}
      </span>
    </label>
  );
}
